import type { LevelSim } from "@highmarch/domain";
import { fmtCompact } from "./kit";
import { portraitImg } from "./portraits";
import { icon } from "./iconsKit";

/**
 * Damage dealt per tower (QoL R2), from the run's host-side tally (LevelSession.damageByTower: the EnemyDamaged
 * events summed, never the sim): the tower sheet's line and the end screen's top towers.
 */

/** A tower's damage this run and its share of every tower's. */
export function damageShare(tally: ReadonlyMap<number, number>, towerId: number): { dealt: number; share: number } {
  let total = 0;
  for (const v of tally.values()) total += v;
  const dealt = tally.get(towerId) ?? 0;
  return { dealt, share: total > 0 ? dealt / total : 0 };
}

/** The sheet's line: "Dealt 12.4k · 23 % of all damage" (a fresh tower: "No damage dealt yet"). */
export function dealtLine(tally: ReadonlyMap<number, number>, towerId: number): string {
  const { dealt, share } = damageShare(tally, towerId);
  return dealt > 0 ? `Dealt <b>${fmtCompact(dealt)}</b> · ${Math.round(share * 100)} % of all damage` : "No damage dealt yet";
}

/** One row of the end screen's top towers. */
export interface TowerDamageRow { readonly towerId: number; readonly id: string; readonly name: string; readonly tier: number; readonly dealt: number; readonly share: number }

/** How many towers the end screen ranks. */
export const TOP_TOWERS = 3;

/** The run's towers by damage dealt, most first (at most `n`); a demolished tower keeps its damage but has no row. */
export function topTowers(sim: LevelSim, tally: ReadonlyMap<number, number>, n = TOP_TOWERS): TowerDamageRow[] {
  let total = 0;
  for (const v of tally.values()) total += v;
  const rows: TowerDamageRow[] = [];
  for (const t of sim.towerSlots) {
    const dealt = tally.get(t.id) ?? 0;
    if (!t.alive || dealt <= 0) continue;
    const a = sim.balance.towers[t.archetype];
    rows.push({ towerId: t.id, id: a.id, name: a.name, tier: t.tier, dealt, share: total > 0 ? dealt / total : 0 });
  }
  rows.sort((a, b) => b.dealt - a.dealt || a.towerId - b.towerId);
  return rows.slice(0, n);
}

/** Portrait size in the end screen's cells. */
const CELL_PORTRAIT_PX = 30;

/**
 * The end screen's "Top towers" strip: up to three cells side by side — portrait, damage, share bar — so it costs one
 * short row on a 375 × 667 screen. Empty when nothing dealt damage.
 */
export function topTowersHtml(rows: readonly TowerDamageRow[]): string {
  if (rows.length === 0) return "";
  const cells = rows.map((r) => `<div class="tdmg" title="${r.name} · tier ${r.tier}"><span class="pt">${portraitImg(r.id, CELL_PORTRAIT_PX, icon("tower", 18, "#fff8e6"), "", r.tier)}</span><span class="val"><b class="disp">${fmtCompact(r.dealt)}</b><small>${Math.round(r.share * 100)} %</small><i class="bar"><i style="width:${Math.round(r.share * 100)}%"></i></i></span></div>`).join("");
  return `<div class="top-towers"><small class="cap">TOP TOWERS</small><div class="cells">${cells}</div></div>`;
}
